export const INTAKE_HISTORY_STATE_KEY = "__lucasClinicalProfileIntake";
export const ACCOUNT_HISTORY_STATE_KEY = "__lucasClinicalProfileAccountInvite";

const INTAKE_QUERY_KEYS = ["cpIntakeToken", "cpIntakeId", "cpProviderOrg"];
const ACCOUNT_QUERY_KEYS = ["cpInviteToken", "cpProfileId", "cpProviderOrg"];
// cpProviderOrg is shared by both URL shapes, so it cannot identify which
// credential set owns the link. Using it as a discriminator lets the intake
// scrubber consume it from an account-activation URL (and vice versa), leaving
// the intended flow with an incomplete context.
const INTAKE_PURPOSE_KEYS = ["cpIntakeToken", "cpIntakeId"];
const ACCOUNT_PURPOSE_KEYS = ["cpInviteToken", "cpProfileId"];
const SENSITIVE_QUERY_KEYS = [...INTAKE_QUERY_KEYS, ...ACCOUNT_QUERY_KEYS, "token", "data", "email", "phone", "referralUserId"];

const emptyContext = () => ({ present: false, intakeToken: "", intakeId: "", providerOrg: "" });
const emptyAccountContext = () => ({ present: false, inviteToken: "", profileId: "", providerOrg: "" });

// index.js scrubs bearer credentials before Sentry and React Router start.
// React Router can replace the current history.state during initialization,
// so keep a one-time, in-memory handoff until the owning route mounts and
// writes the context back into the router-owned history entry. This is scoped
// to the current document: nothing is written to localStorage/sessionStorage,
// and a later unrelated route cannot inherit a consumed credential.
let stagedIntakeContext = emptyContext();
let stagedAccountContext = emptyAccountContext();

const readQueryContext = (search) => {
  const params = new URLSearchParams(search || "");
  const present = INTAKE_PURPOSE_KEYS.some((key) => params.has(key));
  if (!present) return emptyContext();
  return {
    present: true,
    intakeToken: params.get("cpIntakeToken") || "",
    intakeId: params.get("cpIntakeId") || "",
    providerOrg: params.get("cpProviderOrg") || "",
  };
};

const readHistoryContext = () => {
  if (typeof window === "undefined") return emptyContext();
  const stored = window.history.state?.[INTAKE_HISTORY_STATE_KEY];
  if (!stored?.present) return emptyContext();
  return {
    present: true,
    intakeToken: typeof stored.intakeToken === "string" ? stored.intakeToken : "",
    intakeId: typeof stored.intakeId === "string" ? stored.intakeId : "",
    providerOrg: typeof stored.providerOrg === "string" ? stored.providerOrg : "",
  };
};

const readAccountQueryContext = (search) => {
  const params = new URLSearchParams(search || "");
  const present = ACCOUNT_PURPOSE_KEYS.some((key) => params.has(key));
  if (!present) return emptyAccountContext();
  return {
    present: true,
    inviteToken: params.get("cpInviteToken") || "",
    profileId: params.get("cpProfileId") || "",
    providerOrg: params.get("cpProviderOrg") || "",
  };
};

const readAccountHistoryContext = () => {
  if (typeof window === "undefined") return emptyAccountContext();
  const stored = window.history.state?.[ACCOUNT_HISTORY_STATE_KEY];
  if (!stored?.present) return emptyAccountContext();
  return {
    present: true,
    inviteToken: typeof stored.inviteToken === "string" ? stored.inviteToken : "",
    profileId: typeof stored.profileId === "string" ? stored.profileId : "",
    providerOrg: typeof stored.providerOrg === "string" ? stored.providerOrg : "",
  };
};

export const readClinicalProfileIntakeContext = (search) => {
  const fromQuery = readQueryContext(search ?? (typeof window !== "undefined" ? window.location.search : ""));
  if (fromQuery.present) return fromQuery;
  const fromHistory = readHistoryContext();
  return fromHistory.present ? fromHistory : stagedIntakeContext;
};

export const readClinicalProfileAccountInviteContext = (search) => {
  const fromQuery = readAccountQueryContext(search ?? (typeof window !== "undefined" ? window.location.search : ""));
  if (fromQuery.present) return fromQuery;
  const fromHistory = readAccountHistoryContext();
  return fromHistory.present ? fromHistory : stagedAccountContext;
};

// Run once before telemetry initialization and again at the portal boundary.
// Credentials stay on the current browser-history entry so a refresh can
// resume, but never remain in a URL that can reach referrers or tracing tools.
export const captureClinicalProfileIntakeContext = (search, { stageForRouter = false } = {}) => {
  if (typeof window === "undefined") return emptyContext();
  const fromQuery = readQueryContext(search ?? window.location.search);
  const fromHistory = fromQuery.present ? emptyContext() : readHistoryContext();
  const captured = fromQuery.present ? fromQuery : fromHistory.present ? fromHistory : stagedIntakeContext;
  if (!captured.present) return emptyContext();

  if (stageForRouter) stagedIntakeContext = captured;

  const currentState = window.history.state && typeof window.history.state === "object" ? window.history.state : {};
  const url = new URL(window.location.href);
  INTAKE_QUERY_KEYS.forEach((key) => url.searchParams.delete(key));
  window.history.replaceState(
    { ...currentState, [INTAKE_HISTORY_STATE_KEY]: captured },
    document.title,
    url.pathname + url.search + url.hash
  );
  // The route-level capture runs after React Router owns history.state. Once
  // it has persisted the staged context there, do not let another SPA route
  // inherit the bearer credential from module memory.
  if (!stageForRouter) stagedIntakeContext = emptyContext();
  return captured;
};

// Account activation is commonly opened from an SMS into a mobile browser.
// Mobile suspension/reload must not discard the one-time account credential
// after it has been removed from the address bar.
export const captureClinicalProfileAccountInviteContext = (search, { stageForRouter = false } = {}) => {
  if (typeof window === "undefined") return emptyAccountContext();
  const fromQuery = readAccountQueryContext(search ?? window.location.search);
  const fromHistory = fromQuery.present ? emptyAccountContext() : readAccountHistoryContext();
  const captured = fromQuery.present ? fromQuery : fromHistory.present ? fromHistory : stagedAccountContext;
  if (!captured.present) return emptyAccountContext();

  if (stageForRouter) stagedAccountContext = captured;

  const currentState = window.history.state && typeof window.history.state === "object" ? window.history.state : {};
  const url = new URL(window.location.href);
  ACCOUNT_QUERY_KEYS.forEach((key) => url.searchParams.delete(key));
  window.history.replaceState(
    { ...currentState, [ACCOUNT_HISTORY_STATE_KEY]: captured },
    document.title,
    url.pathname + url.search + url.hash
  );
  if (!stageForRouter) stagedAccountContext = emptyAccountContext();
  return captured;
};

export const redactSensitiveUrl = (rawUrl) => {
  if (typeof rawUrl !== "string" || !rawUrl) return rawUrl;
  try {
    const absolute = /^[a-z][a-z\d+.-]*:/i.test(rawUrl);
    const base = typeof window !== "undefined" ? window.location.origin : "https://redaction.invalid";
    const url = new URL(rawUrl, base);
    SENSITIVE_QUERY_KEYS.forEach((key) => {
      if (url.searchParams.has(key)) url.searchParams.set(key, "[REDACTED]");
    });
    return absolute ? url.toString() : url.pathname + url.search + url.hash;
  } catch {
    return SENSITIVE_QUERY_KEYS.some((key) => rawUrl.includes(`${key}=`)) ? "[REDACTED-URL]" : rawUrl;
  }
};
